import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { layoutShopSheetRadius, lead800, lead900, navBarInactiveCircle } from '../theme/colors';

const slides = [1, 2, 3];
const repeatCount = 7;
const middleRepeat = Math.floor(repeatCount / 2);
const initialSlide = 0;
const carouselSlides = Array.from({ length: repeatCount }, () => slides).flat();
const slideColors = {
  1: lead900,
  2: lead800,
  3: navBarInactiveCircle,
};

const AUTOPLAY_MS = 8000;

const WelcomeCarousel = () => {
  const scrollRef = useRef(null);
  const snapTimer = useRef(null);
  const autoplayTimer = useRef(null);
  const bulletScrollUnlockTimer = useRef(null);
  const pendingBulletIndex = useRef(null);
  const isInitialized = useRef(false);
  const dragStartX = useRef(0);
  const dragStartScroll = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [activeSlide, setActiveSlide] = useState(initialSlide);

  const getClosestVirtualIndex = () => {
    const carousel = scrollRef.current;

    if (!carousel?.children.length) return 0;

    const carouselCenter = carousel.scrollLeft + carousel.clientWidth / 2;

    return Array.from(carousel.children).reduce((closest, child, index) => {
      const childCenter = child.offsetLeft + child.clientWidth / 2;
      const closestChild = carousel.children[closest];
      const closestCenter = closestChild.offsetLeft + closestChild.clientWidth / 2;

      return Math.abs(carouselCenter - childCenter) < Math.abs(carouselCenter - closestCenter) ? index : closest;
    }, 0);
  };

  const updateActiveSlide = () => {
    setActiveSlide(getClosestVirtualIndex() % slides.length);
  };

  const centerSlide = (virtualIndex, behavior = 'auto') => {
    const carousel = scrollRef.current;
    const slide = carousel?.children[virtualIndex];

    if (!carousel || !slide) return;

    const left = slide.offsetLeft - (carousel.clientWidth - slide.clientWidth) / 2;
    carousel.scrollTo({ left, behavior });
  };

  const clearAutoplay = () => {
    window.clearTimeout(autoplayTimer.current);
  };

  const scheduleAutoplay = () => {
    clearAutoplay();
    autoplayTimer.current = window.setTimeout(() => {
      if (!isInitialized.current || isDragging || pendingBulletIndex.current !== null) {
        scheduleAutoplay();
        return;
      }

      const nextVirtual = getClosestVirtualIndex() + 1;
      const nextBullet = nextVirtual % slides.length;

      pendingBulletIndex.current = nextBullet;
      setActiveSlide(nextBullet);
      centerSlide(nextVirtual, 'smooth');
      scheduleAutoplay();
    }, AUTOPLAY_MS);
  };

  useLayoutEffect(() => {
    const carousel = scrollRef.current;
    const virtualIndex = middleRepeat * slides.length + initialSlide;
    const slide = carousel?.children[virtualIndex];

    if (carousel && slide) {
      const left = slide.offsetLeft - (carousel.clientWidth - slide.clientWidth) / 2;
      carousel.scrollLeft = left;
      setActiveSlide(initialSlide);
    }

    isInitialized.current = true;
    setIsReady(true);

    return () => {
      window.clearTimeout(snapTimer.current);
      window.clearTimeout(bulletScrollUnlockTimer.current);
      clearAutoplay();
    };
  }, []);

  useEffect(() => {
    if (!isReady) return undefined;

    scheduleAutoplay();

    return clearAutoplay;
  }, [isReady]);

  useEffect(() => {
    const carousel = scrollRef.current;

    const handleScrollEnd = () => {
      if (pendingBulletIndex.current !== null) {
        window.clearTimeout(bulletScrollUnlockTimer.current);
        setActiveSlide(pendingBulletIndex.current);
        pendingBulletIndex.current = null;
        return;
      }

      if (!isDragging) {
        updateActiveSlide();
      }
    };

    carousel?.addEventListener('scrollend', handleScrollEnd);

    return () => {
      carousel?.removeEventListener('scrollend', handleScrollEnd);
    };
  }, [isDragging]);

  const handleScroll = () => {
    if (!isInitialized.current) return;

    const carousel = scrollRef.current;

    if (!carousel) return;

    const firstSlide = carousel.children[0];
    const secondSlide = carousel.children[1];

    if (!firstSlide || !secondSlide) return;

    const slideStep = secondSlide.offsetLeft - firstSlide.offsetLeft;
    const groupWidth = slideStep * slides.length;
    const minimumScroll = groupWidth;
    const maximumScroll = groupWidth * (repeatCount - 2);

    if (carousel.scrollLeft < minimumScroll) {
      carousel.scrollLeft += groupWidth * middleRepeat;
    }

    if (carousel.scrollLeft > maximumScroll) {
      carousel.scrollLeft -= groupWidth * middleRepeat;
    }

    if (pendingBulletIndex.current !== null) {
      window.clearTimeout(bulletScrollUnlockTimer.current);
      bulletScrollUnlockTimer.current = window.setTimeout(() => {
        if (pendingBulletIndex.current === null) return;

        setActiveSlide(pendingBulletIndex.current);
        pendingBulletIndex.current = null;
      }, 150);
      return;
    }

    updateActiveSlide();
  };

  const handlePointerDown = (event) => {
    const carousel = scrollRef.current;

    if (!carousel) return;

    clearAutoplay();
    setIsDragging(true);
    dragStartX.current = event.clientX;
    dragStartScroll.current = carousel.scrollLeft;
    carousel.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const carousel = scrollRef.current;

    if (!isDragging || !carousel) return;

    event.preventDefault();
    carousel.scrollLeft = dragStartScroll.current - (event.clientX - dragStartX.current);
  };

  const handlePointerUp = () => {
    setIsDragging(false);

    window.clearTimeout(snapTimer.current);
    snapTimer.current = window.setTimeout(() => {
      const carousel = scrollRef.current;

      if (!carousel) return;

      const closestIndex = getClosestVirtualIndex();

      centerSlide(closestIndex, 'smooth');
      setActiveSlide(closestIndex % slides.length);
      scheduleAutoplay();
    }, 0);
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        visibility: isReady ? 'visible' : 'hidden',
      }}
    >
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onMouseEnter={clearAutoplay}
        onMouseLeave={scheduleAutoplay}
        sx={{
          width: '100%',
          height: { xs: '176px', sm: '220px' },
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          overflowY: 'hidden',
          px: { xs: 0, sm: '9%' },
          scrollSnapType: isDragging ? 'none' : 'x mandatory',
          scrollPaddingX: { xs: 0, sm: '9%' },
          scrollBehavior: !isReady || isDragging ? 'auto' : 'smooth',
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'pan-x',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
        }}
      >
        {carouselSlides.map((slide, index) => (
          <Box
            key={`${slide}-${index}`}
            sx={{
              flex: '0 0 82%',
              height: '100%',
              borderRadius: layoutShopSheetRadius,
              bgcolor: slideColors[slide],
              scrollSnapAlign: 'center',
              userSelect: 'none',
            }}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {slides.map((slide, index) => (
          <Box
            component="button"
            key={slide}
            type="button"
            aria-label={`Ir para anúncio ${index + 1}`}
            aria-current={activeSlide === index ? 'true' : undefined}
            onClick={() => {
              clearAutoplay();
              window.clearTimeout(bulletScrollUnlockTimer.current);
              pendingBulletIndex.current = index;
              setActiveSlide(index);
              centerSlide(middleRepeat * slides.length + index, 'smooth');
              scheduleAutoplay();
            }}
            sx={{
              width: 14,
              height: 14,
              border: 0,
              borderRadius: '50%',
              bgcolor: activeSlide === index ? 'primary.main' : navBarInactiveCircle,
              cursor: 'pointer',
              transition: 'background-color 160ms ease, transform 160ms ease',
              '&:hover': {
                transform: 'scale(1.08)',
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default WelcomeCarousel;
